import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSEO from '@/hooks/useSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { categoryMap } from '@/mocks/categories';

const ArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const video = getVideoById(Number(id));

  const relatedVideos = video ? getRelatedVideos(video, 6) : [];
  const categoryName = video ? (categoryMap[video.category] || video.category) : '';
  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  const articleSchema = video ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.releasedAt,
    author: {
      '@type': 'Person',
      name: video.director,
    },
  } : null;

  useSEO({
    title: video ? `${video.title} - 红桃视频免费在线观看` : '影片 - 红桃视频',
    description: video ? video.description : '红桃视频免费在线观看',
    keywords: video ? `红桃视频${video.title},${video.title}在线观看,${video.title}高清免费,${video.tags.join(',')}` : '红桃视频',
    canonicalUrl: `https://hongtao.video/article/${id}`,
    ogTitle: video ? `${video.title} - 红桃视频免费在线观看` : '影片 - 红桃视频',
    ogDescription: video ? video.description : '红桃视频免费在线观看',
    ogUrl: `https://hongtao.video/article/${id}`,
    structuredData: articleSchema ? [articleSchema] : [],
  });

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            影片未找到
          </h2>
          <p className="text-foreground-500 mb-6">您访问的影片不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), to: '/' },
            { label: t('category.title'), to: '/category/all' },
            { label: categoryName, to: `/category/${video.category}` },
            { label: video.title },
          ]}
        />
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <div className="relative overflow-hidden rounded-xl aspect-video bg-background-200 mb-6">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer group">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-play-fill text-4xl ml-1"></i>
                </div>
              </div>
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              {video.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">
              {video.updateEpisode}
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              {video.isHot && (
                <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  热门推荐
                </span>
              )}
              {video.isNew && (
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  最新上线
                </span>
              )}
              <span className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-star-fill text-accent-500"></i>
                <strong>{video.rating}</strong>
              </span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600">{formatViews(video.views)}次播放</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 p-4 rounded-xl bg-background-100/70">
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('article.director')}</p>
                <p className="text-sm font-medium text-foreground-900">{video.director}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('article.actors')}</p>
                <p className="text-sm font-medium text-foreground-900">{video.actors.join('、')}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('article.region')}</p>
                <p className="text-sm font-medium text-foreground-900">{video.region}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('article.year')} / {t('article.duration')}</p>
                <p className="text-sm font-medium text-foreground-900">{video.year} / {video.duration}</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex flex-wrap gap-1.5 mb-4">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-base font-bold text-foreground-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <div className="w-1 h-4 rounded-full bg-primary-500" />
                {t('article.description')}
              </h2>
              <div className="text-sm text-foreground-700 leading-relaxed bg-background-100/70 rounded-xl p-5">
                <p>{video.content}</p>
              </div>
            </div>
          </div>

          <div className="lg:w-[340px] flex-shrink-0">
            <div className="sticky top-20">
              <div className="bg-background-100/70 rounded-xl p-5 mb-6">
                <Link
                  to={`/article/${video.id}`}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer mb-3"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  {t('article.watchNow')}
                </Link>
                <Link
                  to="/category/all"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-background-50 hover:bg-background-200 text-foreground-700 rounded-full text-sm border border-background-300/60 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-grid-line"></i>
                  浏览更多影片
                </Link>
              </div>

              <h3 className="text-base font-bold text-foreground-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <div className="w-1 h-4 rounded-full bg-accent-500" />
                {t('article.related')}
              </h3>
              <div className="space-y-3">
                {relatedVideos.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="flex gap-3 group cursor-pointer"
                  >
                    <div className="w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors duration-200">
                        {rel.title}
                      </h4>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {rel.rating}
                        </span>
                        <span className="text-foreground-300">|</span>
                        <span>{rel.year}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;